import {eye} from 'assets/images/images';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from 'components';
import CardSummary from 'components/CardSummary';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Constants} from '../../helpers';
import {useSelector} from 'react-redux';
import {PageContext} from '../../helpers/context';
import {transformObject} from '../FraudDetection/Flows/utils';
import {useFlowsLogic} from '../FraudDetection/Flows/hooks/useFlows';
import {
  fetchAccountsTierOverview,
  fetchProfiles,
} from '../../requests/queries/flows';
import PageNumber from '../../components/PageNumber';
import FilterV2 from '../../components/FilterV2';
import moment from 'moment';
import {calculateTotals, getSeverityColor} from './helpers';
import {useGatedQuery} from '../FraudDetection/Flows/hooks/useGatedQuery';

function ProfiledRiskProfiles() {
  const {userDetails} = useSelector(state => state?.auth);

  localStorage.removeItem('currentAccount');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    start: null,
    end: null,
  });

  const {data: overviewData, isLoading: overviewLoading} = useGatedQuery(
    ['accounts-tier'],
    () => fetchAccountsTierOverview(),
  );

  const RISK_TIER = useMemo(() => {
    if (!overviewLoading) {
      const getData = key =>
        calculateTotals(
          overviewData?.entity?.filter(
            data => data?.tier?.toLowerCase() === key,
          ),
          'count',
        );

      return {
        low: getData('low')?.count || 0,
        medium: getData('medium')?.count || 0,
        high: getData('high')?.count || 0,
        veryHigh: getData('very high')?.count || 0,
      };
    }
  }, [overviewData?.entity, overviewLoading]);

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('easydetect');
  }, [setPageName]);

  const params = useMemo(
    () =>
      transformObject({
        company_id: userDetails?.company?.id,
        limit,
        page: currentPage,
        term: searchTerm,
        ...filters,
      }),
    [userDetails?.company?.id, limit, currentPage, searchTerm, filters],
  );

  const {flows} = useFlowsLogic({isFilter: true});

  const projectFilters = flows?.flows?.map(flow => ({
    label: flow.name,
    value: flow.id,
  }));

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  const handleFilterChange = values => {
    const {dateRange, ...rest} = values;
    delete rest.date;

    setFilters({...rest, date_from: dateRange?.start, date_to: dateRange?.end});
    setCurrentPage(1);
  };

  const filterOptions = useMemo(
    () => [
      {
        key: 'project_id',
        label: 'Flows',
        type: 'select',
        multiSelect: false,
        options: projectFilters,
      },
    ],
    [projectFilters],
  );

  const {data: accountsData, isLoading} = useGatedQuery(
    ['profiles', {params}],
    () => fetchProfiles({params}),
    {
      enabled: userDetails?.company?.id?.length > 0,
    },
  );

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const PROFILES = useMemo(
    () => (!isLoading && accountsData && accountsData?.rows) || [],
    [accountsData, isLoading],
  );

  const handleRowClick = profileId => {
    navigate(
      Constants.PAGES_URL.PROFILED_RISK_PROFILE_DETAILS.replace(
        ':slug',
        profileId,
      ),
      {
        state: {
          profile_id: profileId,
        },
      },
    );
  };

  return (
    <DashboardLayout
      bg="bg-white"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Profiles</p>
      }
    >
      <div className="flex items-center justify-between py-2 flex-wrap gap-3 mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <SearchInput
          onChange={value => setSearchTerm(value)}
          defaultValue={searchTerm}
        />
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>
      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 filters={filterOptions} onFilterChange={handleFilterChange} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-center gap-2 pt-5 pb-6">
        <CardSummary
          title="Low risk"
          body={RISK_TIER?.low}
          loading={overviewLoading}
        />
        <CardSummary
          title="Medium risk"
          body={RISK_TIER?.medium}
          loading={overviewLoading}
        />
        <CardSummary
          title="High risk"
          body={RISK_TIER?.high}
          loading={overviewLoading}
        />
        <CardSummary
          title="Very high risk"
          body={RISK_TIER?.veryHigh}
          loading={overviewLoading}
        />
      </div>
      <TableLayout negativeMargins>
        <thead className="font-semibold uppercase">
          <tr className="bg-white80">
            <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[16%]">
              Name
            </th>
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[8%]">
              Profile ID
            </th>
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[8%]">
              Score
            </th>
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[8%]">
              Tier
            </th>
            {/*<th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[8%]">*/}
            {/*  Open Cases*/}
            {/*</th>*/}
            {/*<th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[20%]">*/}
            {/*  Use Cases*/}
            {/*</th>*/}
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[12%]">
              Date Created
            </th>
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[12%]">
              Last Decision
            </th>
            <th className="pl-0 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap w-[8%]">
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td colSpan={8}>
              {isLoading ? (
                <Loader height={65} />
              ) : !isLoading && PROFILES?.length === 0 ? (
                <EmptyState
                  body="You don't have any profiles yet, create a flow to get started"
                  buttonText={'Create a flow'}
                  onClick={() => navigate(Constants.PAGES_URL.FLOWS)}
                  height={65}
                  bodyMaxWidth="max-w-[278px]"
                />
              ) : null}
            </td>
          </tr>
        </tbody>

        <tbody className="text-sm bg-white">
          {PROFILES &&
            PROFILES?.map(profile => {
              const tierTextColor = getSeverityColor(profile?.tier, 'solid');
              const tierBgColor = getSeverityColor(profile?.tier, 'tint');

              return (
                <tr
                  key={profile?.id}
                  onClick={() => handleRowClick(profile?.id)}
                  className="border-[0.5px] border-[#E4E3EC] cursor-pointer"
                >
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize w-[16%]">
                    {profile?.user_name || 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[8%] uppercase">
                    {profile?.id || 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[8%]">
                    {profile?.risk_score || 0}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[8%]">
                    <span
                      className={`py-1 px-2 font-medium text-[10px] rounded-full`}
                      style={{
                        color: tierTextColor,
                        backgroundColor: tierBgColor,
                      }}
                    >
                      {profile?.tier || 'N/A'}
                    </span>
                  </td>
                  {/*<td className="p-4 pl-0 text-body whitespace-nowrap w-[8%]">*/}
                  {/*  {profile?.open_cases || '0'}*/}
                  {/*</td>*/}
                  {/*<td className="p-4 pl-0 text-body whitespace-nowrap w-[20%]">*/}
                  {/*  <div className="flex flex-wrap gap-2">*/}
                  {/*    <span className="py-1 px-2 font-medium text-[10px] text-black bg-[#ECECEC] rounded-full">*/}
                  {/*      N/A*/}
                  {/*    </span>*/}
                  {/*    <span className="py-1 px-2 font-medium text-[10px] text-black bg-[#ECECEC] rounded-full">*/}
                  {/*      N/A*/}
                  {/*    </span>*/}
                  {/*  </div>*/}
                  {/*</td>*/}
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[12%]">
                    {profile?.createdAt
                      ? moment(profile?.createdAt).format('Do MMM YYYY HH:mm')
                      : 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[12%]">
                    {profile?.updatedAt
                      ? moment(profile?.updatedAt).format('Do MMM YYYY HH:mm')
                      : 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap w-[8%]">
                    <button title="View" className="h-4 aspect-square">
                      <img src={eye} alt="View" />
                    </button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </TableLayout>

      {!isLoading && PROFILES.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
          <PageNumber
            currentPage={currentPage}
            limit={limit}
            totalCount={accountsData?.count || 0}
          />

          <div className="flex items-center gap-5">
            <PageLimit
              onLimitChange={setLimit}
              total={accountsData.count}
              length={PROFILES.length}
              limit={limit}
            />

            <Pagination
              total={Math.ceil(accountsData.count / limit)}
              current={+currentPage}
              onPageChange={activePage => {
                localStorage.setItem('page', String(activePage));
                pageClick(activePage);
              }}
            />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default ProfiledRiskProfiles;
